import { Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import RichText, { domToReact } from '@/components/RichText/RichText';
import PriceCatalog from '@/components/ui/PriceCatalog/PriceCatalog';
import { PRICE_CATALOG_REQUEST_TYPES } from '@/helpers/constants';
import dametisApi from '@/helpers/dametisApi';

import { Wrapper } from './DametisProductCatalog.style';
import productPlaceholder from './lib/product-placeholder.png';

const prepareProducts = (products, attributes) => {
  const attributeNames = attributes
    .filter(attribute => !!attribute)
    .map(attribute => attribute.attributes.blob.name);

  const prepareAttributes = attributes =>
    attributes
      .filter(({ key }) => attributeNames.includes(key))
      .map(({ key, value, unit }) => ({ name: key, value, unit }));

  return products.map(product => ({
    headline: product.name,
    image:
      product.images?.length > 0
        ? product.images[0].url
        : productPlaceholder.src,
    price: product.rental_price,
    initialPrice: product.premium_product_original_price,
    priceBasis: product.rental_price_basis,
    attributes: prepareAttributes(product.attributes),
    salesforceId: product.salesforce_id,
    productReferenceId: product.id?.toString(),
    automatedOffer: Boolean(product.automated_offer),
    sendAutomatedOffer: Boolean(product.send_automated_offer),
    minLeadTime: product.min_lead_time,
    rentalPriceBasis: product.rental_price_basis,
    rentalPrice: product.rental_price,
  }));
};

const richTextReplacer = (element, wrappedReplacer) => {
  if (element.name === 'p' && element.children.length) {
    return (
      <Typography mt={2}>
        {domToReact(element.children, { replace: wrappedReplacer })}
      </Typography>
    );
  }

  return null;
};

/**
 * DametisProductCatalog
 */
const DametisProductCatalog = ({
  productGroupId,
  productGroupName,
  attributes,
  productGroupDescription,
  requestType,
}) => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    setLoading(true);
    dametisApi
      .getProductTypesByGroup(productGroupId)
      .then(({ data }) => {
        if (Array.isArray(data.data) && data.data.length) {
          setProducts(prepareProducts(data.data, attributes));
        }

        setLoading(false);
      })
      .catch(console.error);
  }, [productGroupId, attributes]);

  return (
    <Wrapper>
      <PriceCatalog
        products={products}
        loading={loading}
        data-cy="PriceCatalogDametis"
        productGroupName={productGroupName}
        productGroupId={productGroupId}
        requestType={requestType}
      />
      {productGroupDescription && (
        <RichText data={productGroupDescription} replace={richTextReplacer} />
      )}
    </Wrapper>
  );
};

DametisProductCatalog.propTypes = {
  productGroupId: PropTypes.number.isRequired,
  productGroupName: PropTypes.string,
  attributes: PropTypes.arrayOf(
    PropTypes.shape({
      attributes: PropTypes.shape({
        blob: PropTypes.shape({
          name: PropTypes.string,
        }),
      }),
    })
  ),
  requestType: PropTypes.oneOf(Object.values(PRICE_CATALOG_REQUEST_TYPES)),
};

DametisProductCatalog.defaultProps = {
  productGroupName: '',
  attributes: [],
};

export default DametisProductCatalog;
